import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Shield,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  Building2,
  Loader2,
  RefreshCw,
  AlertCircle,
  Filter
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRegistrationRequests = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState({});

  const getAuthHeader = () => {
    const token = localStorage.getItem('slotmate_admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/admin/registration-requests?${params}`, {
        headers: getAuthHeader()
      });
      setRequests(response.data.requests);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('slotmate_admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchRequests();
  }, [navigate, fetchRequests]);

  const handleApprove = async (requestId) => {
    setActionLoading(prev => ({ ...prev, [requestId]: 'approve' }));
    try {
      await axios.put(
        `${API_URL}/admin/registration-requests/${requestId}/approve`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Registration approved! User account created.');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleReject = async (requestId) => {
    const reason = window.prompt('Rejection reason (optional):');
    setActionLoading(prev => ({ ...prev, [requestId]: 'reject' }));
    try {
      await axios.put(
        `${API_URL}/admin/registration-requests/${requestId}/reject?reason=${encodeURIComponent(reason || '')}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Registration rejected.');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      pending_verification: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return variants[status] || variants.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending Review',
      pending_verification: 'Email Unverified',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white/60 animate-pulse">Loading registration requests...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchRequests}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="text-white/60 text-sm">Admin Portal</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Registration Requests</h1>
          <p className="text-white/40">Review and manage business registration requests</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['pending', 'pending_verification', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === s
                  ? 'bg-white/20 text-white'
                  : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/60'
              }`}
              data-testid={`filter-${s}`}
            >
              {getStatusLabel(s)} {s === 'pending' && total > 0 && statusFilter === 'pending' ? `(${total})` : ''}
            </button>
          ))}
        </div>

        {/* Requests List */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
          {requests.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40">No {statusFilter} requests found</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {requests.map((req) => (
                <div
                  key={req.id}
                  className="p-5 hover:bg-white/5 transition-colors"
                  data-testid={`request-row-${req.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{req.business_name}</h3>
                          <Badge className={getStatusBadge(req.status)}>
                            {getStatusLabel(req.status)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-white/50">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {req.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {req.mobile}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {req.email_verified && (
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-emerald-400">
                          <CheckCircle className="w-3 h-3" />
                          Email verified
                        </span>
                      )}
                      {req.rejection_reason && (
                        <p className="mt-2 text-sm text-red-400/70">
                          Reason: {req.rejection_reason}
                        </p>
                      )}
                    </div>

                    {req.status === 'pending' && (
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(req.id)}
                          disabled={!!actionLoading[req.id]}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`approve-btn-${req.id}`}
                        >
                          {actionLoading[req.id] === 'approve' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(req.id)}
                          disabled={!!actionLoading[req.id]}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          data-testid={`reject-btn-${req.id}`}
                        >
                          {actionLoading[req.id] === 'reject' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminRegistrationRequests;
